import { useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Dialog from "@/components/Dialog";
import { showSnackbar } from "@/lib/snackbarUtils";
import { OPEN_ORDER_MUTATION, COMMERCE_AUDIT_LOG_QUERY } from "@/pages/billing/orders/[orderId]/queries";
import { OrderStage } from "@/pages/billing/types";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import type { RegistrationOrder } from "../types";
import ApplicationApprovedOrderPanel, { defaultOrderDueDate } from "./ApplicationApprovedOrderPanel";

interface ApplicationApprovedDialogProps {
  open: boolean;
  applicationId: string;
  registrationOrder: RegistrationOrder;
  upcomingDuesOrder?: RegistrationOrder | null;
  onClose: () => void;
}

interface OrderToOpen {
  commerceOrderId: string;
  dueDate: Date;
  label: string;
}

function isDraftOrder(order: RegistrationOrder, openedOrderIds: ReadonlySet<string>): boolean {
  return order.status === OrderStage.DRAFT && !openedOrderIds.has(order.id);
}

function withOpenedStatus(order: RegistrationOrder, openedOrderIds: ReadonlySet<string>): RegistrationOrder {
  if (!openedOrderIds.has(order.id)) {
    return order;
  }

  return { ...order, status: OrderStage.OPEN };
}

function buildPartialFailureMessage(succeededLabels: string[], failedLabel: string, failedMessage: string): string {
  if (succeededLabels.length === 0) {
    return failedMessage;
  }

  const succeededText = succeededLabels.join(" and ");
  return `${succeededText} opened successfully, but ${failedLabel} failed: ${failedMessage}`;
}

export default function ApplicationApprovedDialog({
  open,
  applicationId,
  registrationOrder,
  upcomingDuesOrder,
  onClose
}: ApplicationApprovedDialogProps) {
  const hasDualOrders = Boolean(upcomingDuesOrder);
  const [registrationDueDate, setRegistrationDueDate] = useState(() => defaultOrderDueDate(registrationOrder.dueDate));
  const [renewalDueDate, setRenewalDueDate] = useState(() =>
    upcomingDuesOrder ? defaultOrderDueDate(upcomingDuesOrder.dueDate) : new Date()
  );
  const [openOrderMutation, { loading }] = useMutation(OPEN_ORDER_MUTATION, {
    refetchQueries: [
      COMMERCE_AUDIT_LOG_QUERY,
      { query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }
    ]
  });
  const [errorContent, setErrorContent] = useState("");
  const [ordersOpened, setOrdersOpened] = useState(false);
  const [openedOrderIds, setOpenedOrderIds] = useState<Set<string>>(() => new Set());

  async function handleOpenAndSendOrders() {
    setErrorContent("");

    const ordersToOpen: OrderToOpen[] = [];

    if (isDraftOrder(registrationOrder, openedOrderIds)) {
      ordersToOpen.push({
        commerceOrderId: registrationOrder.id,
        dueDate: registrationDueDate,
        label: "Registration order"
      });
    }

    if (upcomingDuesOrder && isDraftOrder(upcomingDuesOrder, openedOrderIds)) {
      ordersToOpen.push({
        commerceOrderId: upcomingDuesOrder.id,
        dueDate: renewalDueDate,
        label: "Renewal order"
      });
    }

    if (ordersToOpen.length === 0) {
      showSnackbar(hasDualOrders ? "Orders Opened" : "Order Opened");
      setOrdersOpened(true);
      onClose();
      return;
    }

    const succeededLabels: string[] = [];
    const newOpenedIds = new Set(openedOrderIds);

    for (const orderInput of ordersToOpen) {
      try {
        const { data } = await openOrderMutation({
          variables: {
            input: {
              commerceOrderId: orderInput.commerceOrderId,
              dueDate: orderInput.dueDate,
              sendNotification: true
            }
          }
        });

        if (!data?.openCommerceOrder.success) {
          setOpenedOrderIds(newOpenedIds);
          setErrorContent(
            buildPartialFailureMessage(
              succeededLabels,
              orderInput.label,
              data?.openCommerceOrder.message ?? "Failed to open order"
            )
          );
          return;
        }

        succeededLabels.push(orderInput.label);
        newOpenedIds.add(orderInput.commerceOrderId);
      } catch (error) {
        setOpenedOrderIds(newOpenedIds);
        const message = error instanceof Error ? error.message : "Failed to open order";
        setErrorContent(buildPartialFailureMessage(succeededLabels, orderInput.label, message));
        return;
      }
    }

    setOpenedOrderIds(newOpenedIds);
    showSnackbar(hasDualOrders ? "Orders Opened" : "Order Opened");
    setOrdersOpened(true);
    onClose();
  }

  const effectiveRegistrationOrder = withOpenedStatus(registrationOrder, openedOrderIds);
  const effectiveUpcomingDuesOrder = upcomingDuesOrder ? withOpenedStatus(upcomingDuesOrder, openedOrderIds) : null;

  const hasPendingOrders =
    isDraftOrder(registrationOrder, openedOrderIds) ||
    (upcomingDuesOrder != null && isDraftOrder(upcomingDuesOrder, openedOrderIds));

  const canOpenOrders =
    Boolean(registrationDueDate) && (!hasDualOrders || Boolean(renewalDueDate)) && hasPendingOrders && !ordersOpened;

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Application Approved"
      className="w-[600px] max-w-[95vw] max-h-[85vh]"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="application-approved-close">
            Close
          </Button>
          <Button
            variant="filled"
            onClick={handleOpenAndSendOrders}
            disabled={loading || !canOpenOrders}
            testId="application-approved-open-and-send-orders"
          >
            {hasDualOrders ? "Open and Send Orders" : "Open and Send Order"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6" data-testid="application-approved-dialog">
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          The application has been successfully approved and{" "}
          {hasDualOrders ? (
            <>
              <span className="font-bold">draft orders</span> for program fees have automatically been created.
            </>
          ) : (
            <>
              a <span className="font-bold">draft order</span> for program fees has automatically been created.
            </>
          )}{" "}
          The applicant has been sent an email and their account has been updated with their new status.
        </p>

        {errorContent ? (
          <Banner variant="error" description={errorContent} inline closable={false} hideSecondaryDismiss />
        ) : null}

        <div className="flex flex-col gap-10">
          <ApplicationApprovedOrderPanel
            order={effectiveRegistrationOrder}
            {...(hasDualOrders
              ? {
                  label: "This order is for the current membership year (registration)."
                }
              : {})}
            editButtonTestId="application-approved-edit-registration-order"
            dueDate={registrationDueDate}
            onDueDateChange={setRegistrationDueDate}
          />

          {effectiveUpcomingDuesOrder ? (
            <ApplicationApprovedOrderPanel
              order={effectiveUpcomingDuesOrder}
              label="This order is for the upcoming membership year (renewal)."
              editButtonTestId="application-approved-edit-renewal-order"
              dueDate={renewalDueDate}
              onDueDateChange={setRenewalDueDate}
            />
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
